import { useCallback, useEffect, useMemo, useState } from "react";
import { useIso2700127701DebouncedValue, useIso2700127701LatestRequest } from "../../../components/shared/useIso2700127701FilteredRequest";
import { getIso2700127701Soa } from "../api/soa";
import type { Iso2700127701SoaFilters, Iso2700127701SoaResponse } from "../../../types/iso2700127701";

export function useIso2700127701Soa(filters: Iso2700127701SoaFilters) {
  const [data, setData] = useState<Iso2700127701SoaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debouncedSearch = useIso2700127701DebouncedValue(filters.search.trim(), 300);
  const { runLatest, hasActiveRequest } = useIso2700127701LatestRequest();
  const requestFilters = useMemo<Iso2700127701SoaFilters>(() => ({ ...filters, search: debouncedSearch }), [debouncedSearch, filters.grupo, filters.responsables, filters.actualizacion_2026]);
  const requestKey = useMemo(() => JSON.stringify(requestFilters), [requestFilters]);

  const reload = useCallback(async (force = true) => {
    setLoading(true);
    setError(null);
    try {
      const response = await runLatest(requestKey, (signal) => getIso2700127701Soa(requestFilters, signal), force);
      if (response) setData(response);
    } catch {
      setError("No se pudieron cargar los datos de SOA.");
    } finally {
      if (!hasActiveRequest()) setLoading(false);
    }
  }, [hasActiveRequest, requestFilters, requestKey, runLatest]);

  useEffect(() => { void reload(false); }, [reload]);

  return {
    soaRows: data?.data.soa ?? [], planRows: data?.data.plan_anexo_a ?? [], controlModificaciones: data?.data.control_modificaciones ?? [],
    filterOptions: data?.filter_options ?? { grupos: [], responsables: [], actualizaciones_2026: [] },
    meta: data?.meta ?? { soa_total: 0, plan_anexo_a_total: 0 }, loading, error, reload,
  };
}
